import * as _ from 'lodash';
import type { FunctionComponent } from 'react';

import { usePlanSelectorContext } from '../../../contexts/PlanSelectorContext';
import PaidPill from '../Pill/PaidPill';
import ReadOnlySection from '../ReadOnlySection';
import type { FossaPlanNameWithEnterprise } from '../../../../shared/subscriptions/consts';
import type { FossaFeature } from '../../settings/billing/features/consts';

import styles from './PaidFeatureGate.module.less';

type Props = {
  /**
   * Whether or not we should restrict access to the children.
   */
  isRestricted: boolean;
  /**
   * Since this component is basically a wrapper around `window.showPlanSelectorModal`, we should pass in a plan name to
   * show the user when the upgrade modal appears.
   */
  planToShow?: FossaPlanNameWithEnterprise;
  /**
   * The feature to focus on when showing the plan selector modal.
   */
  modalFocusFeature?: FossaFeature;
  /**
   * Hide the PaidPill. This is useful when the styling for placing the PaidPill doesn't work well out of the box so it
   * needs to be handled manually.
   */
  hidePill?: boolean;
  /**
   * Whether or not to show the PaidFeatureGate icon only.
   */
  iconOnly?: boolean;
  /**
   * Whether or not to use the ReadOnlySection component to disable the children.  Useful when custom styling is needed.
   */
  useReadOnly?: boolean;
};

/**
 * PaidFeatureGate is a section which (when specified) will "grey out"
 * a section of the page to imply that it is restricted, for example due to the user's current subscription tier.
 * When clicked, it will open the subscription upgrade modal.
 * Note that you still need to disable any inputs/buttons in the `children`
 * to fully prevent user interaction.
 *
 * @example
 * const MyComponent = () => {
 *   return (
 *     <PaidFeatureGate isRestricted={!isEnterpriseTier({ plan: window.user.organization.stripe_plan })}>
 *      <FancyEnterpriseOnlyFeature />
 *     </PaidFeatureGate>
 *   );
 * };
 */
const PaidFeatureGate: FunctionComponent<Props> = ({
  children,
  isRestricted,
  planToShow,
  modalFocusFeature,
  hidePill,
  iconOnly,
  useReadOnly = true,
}) => {
  const { showPlanSelectorModal } = usePlanSelectorContext();

  if (!isRestricted) {
    return <>{children}</>;
  }

  return (
    <section
      className={styles.paidFeatureGate}
      onClick={
        isRestricted
          ? (e) => {
              e.stopPropagation();
              e.preventDefault();
              showPlanSelectorModal({
                tier: planToShow,
                focusFeature: modalFocusFeature,
              });
            }
          : _.noop
      }
      aria-hidden
    >
      {!hidePill && (
        <div className={styles.paidPillWrapper}>
          <PaidPill iconOnly={iconOnly} />
        </div>
      )}
      {useReadOnly ? <ReadOnlySection isReadOnly={isRestricted}>{children}</ReadOnlySection> : children}
    </section>
  );
};

export default PaidFeatureGate;
